<template>
  <aside class="admin-sidebar" :class="{ mini: collapsed }">
    <div class="admin-sidebar-brand">
      <div class="logo-gem"><AppIcon name="nav-gem" :size="24" /></div>
      <span v-if="!collapsed" class="logo-text">
        Casino<span class="accent">Script</span><span class="badge">Admin</span>
      </span>
    </div>

    <nav class="sidebar-nav">
      <div class="sidebar-section">
        <router-link
          v-for="item in overviewItems"
          :key="item.to"
          :to="item.to"
          class="sidebar-item"
          :class="{ active: isActive(item.to) }"
        >
          <AppIcon :name="item.icon" :size="18" />
          <span v-if="!collapsed" class="sidebar-label">{{ item.label }}</span>
        </router-link>
      </div>

      <div v-for="section in sections" :key="section.title" class="sidebar-section">
        <div v-if="!collapsed" class="sidebar-section-title">{{ section.title }}</div>
        <router-link
          v-for="item in section.items"
          :key="item.to"
          :to="item.to"
          class="sidebar-item"
          :class="{ active: isActive(item.to) }"
        >
          <AppIcon :name="item.icon" :size="18" />
          <span v-if="!collapsed" class="sidebar-label">{{ item.label }}</span>
        </router-link>
      </div>
    </nav>

    <!-- License status indicator -->
    <div v-if="licenseStatus" class="license-status-bar" :class="licenseStatusClass">
      <span class="license-dot" :class="{ pulse: licenseStatus.status === 'grace' }"></span>
      <template v-if="!collapsed">
        <span class="license-label">{{ licenseStatusLabel }}</span>
        <router-link
          v-if="licenseStatus.status !== 'active'"
          to="/admin/settings"
          class="license-settings-link"
        >
          {{ t('admin.nav.license_fix') }}
        </router-link>
      </template>
    </div>

    <div class="admin-sidebar-footer">
      <div class="admin-avatar">{{ adminInitials }}</div>
      <div v-if="!collapsed" class="admin-footer-info">
        <div class="admin-footer-name">{{ adminName }}</div>
        <div class="admin-footer-role">{{ adminRole }}</div>
      </div>
    </div>

    <div class="sidebar-collapse-area">
      <button
        class="sidebar-collapse-btn"
        :aria-label="collapsed ? t('nav.expand_sidebar') : t('nav.collapse_sidebar')"
        @click="toggle"
      >
        <ChevronsLeft class="collapse-icon" :class="{ flipped: collapsed }" :size="16" />
      </button>
    </div>
  </aside>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import AppIcon from '@/components/AppIcon.vue'
import { ChevronsLeft } from 'lucide-vue-next'
import { useAdminStore } from '@/stores/admin'

const route = useRoute()
const { t } = useI18n()
const adminStore = useAdminStore()
const collapsed = ref(false)

const licenseStatus = computed(() => adminStore.licenseStatus)

const licenseStatusClass = computed(() => {
  switch (licenseStatus.value?.status) {
    case 'active':
      return 'license-active'
    case 'grace':
      return 'license-grace'
    default:
      return 'license-inactive'
  }
})

const licenseStatusLabel = computed(() => {
  switch (licenseStatus.value?.status) {
    case 'active':
      return t('admin.nav.license_active')
    case 'grace':
      return t('admin.nav.license_grace')
    default:
      return t('admin.nav.license_unlicensed')
  }
})

const emit = defineEmits<{
  (e: 'toggle', collapsed: boolean): void
}>()

function toggle() {
  collapsed.value = !collapsed.value
  emit('toggle', collapsed.value)
}

function isActive(path: string): boolean {
  return route.path === path || route.path.startsWith(path + '/')
}

// Stub admin data — will come from a Pinia store once auth is implemented
const adminName = ref('Super Admin')
const adminRole = ref('Administrator')
const adminInitials = computed(() => {
  return adminName.value
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
})

// Permission stub — returns true for now, full RBAC enforcement in later story
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function hasPermission(_permission: string): boolean {
  return true
}

interface NavItem {
  icon: string
  label: string
  to: string
}

interface NavSection {
  title: string
  items: NavItem[]
}

const overviewItems = computed((): NavItem[] => [
  { icon: 'nav-dashboard', label: t('admin.nav.dashboard'), to: '/admin/dashboard' },
])

const sections = computed((): NavSection[] => [
  {
    title: t('admin.nav.section_players'),
    items: [
      { icon: 'nav-players', label: t('admin.nav.all_players'), to: '/admin/players' },
    ],
  },
  {
    title: t('admin.nav.section_games'),
    items: [
      { icon: 'nav-games', label: t('admin.nav.games'), to: '/admin/games' },
      { icon: 'nav-games', label: t('admin.nav.rtp_monitoring'), to: '/admin/rtp-monitoring' },
    ],
  },
  {
    title: t('admin.nav.section_finance'),
    items: [
      { icon: 'nav-finance', label: t('admin.nav.transactions'), to: '/admin/finance' },
      { icon: 'nav-finance', label: t('admin.nav.payments'), to: '/admin/payment-transactions' },
    ],
  },
  {
    title: t('admin.nav.section_marketing'),
    items: [
      { icon: 'nav-bonuses', label: t('admin.nav.bonuses'), to: '/admin/bonuses' },
      { icon: 'nav-bonuses', label: t('admin.nav.push_campaigns'), to: '/admin/push-campaigns' },
      { icon: 'nav-bonuses', label: t('admin.nav.reward_catalog'), to: '/admin/rewards' },
      { icon: 'nav-bonuses', label: t('admin.nav.fulfillment_queue'), to: '/admin/rewards/fulfillment' },
      { icon: 'nav-affiliates', label: t('admin.nav.affiliates'), to: '/admin/affiliates' },
    ],
  },
  {
    title: t('admin.nav.section_compliance'),
    items: [
      { icon: 'nav-audit', label: t('admin.nav.aml_alerts'), to: '/admin/aml-alerts' },
      { icon: 'nav-jurisdiction', label: t('admin.nav.geo_fencing'), to: '/admin/jurisdiction' },
      { icon: 'nav-players', label: t('admin.nav.affordability'), to: '/admin/affordability' },
    ],
  },
  {
    title: t('admin.nav.section_system'),
    items: [
      { icon: 'nav-settings', label: t('admin.nav.settings'), to: '/admin/settings' },
      { icon: 'nav-rbac', label: t('admin.nav.rbac'), to: '/admin/rbac' },
      { icon: 'nav-audit', label: t('admin.nav.audit_log'), to: '/admin/audit-log' },
      { icon: 'nav-content', label: t('admin.nav.content'), to: '/admin/content' },
    ],
  },
])
</script>

<style scoped>
.admin-sidebar {
  width: var(--admin-sidebar-w, 260px);
  background: var(--bg-sidebar, #0b1520);
  border-right: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  overflow-x: hidden;
  transition: width var(--transition-normal, 250ms ease);
  position: relative;
  z-index: 100;
  flex-shrink: 0;
}

.admin-sidebar.mini {
  width: var(--sidebar-collapsed, 64px);
}

/* Brand header */
.admin-sidebar-brand {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px;
  height: var(--header-height, 60px);
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  flex-shrink: 0;
}

.logo-gem {
  width: 30px;
  height: 30px;
  background: var(--accent-primary, #00e701);
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-on-accent, #000);
  font-size: 14px;
  font-weight: 900;
  box-shadow: var(--shadow-glow-green, 0 0 24px rgba(0, 231, 1, 0.35));
  animation: glow 3s ease infinite;
  flex-shrink: 0;
}

@keyframes glow {
  0%,
  100% {
    box-shadow: var(--shadow-glow-green, 0 0 24px rgba(0, 231, 1, 0.35));
  }
  50% {
    box-shadow: 0 0 40px rgba(0, 231, 1, 0.6);
  }
}

.logo-text {
  font-family: var(--font-display, 'Russo One', sans-serif);
  font-size: 15px;
  font-weight: 700;
  color: var(--text-primary, #fff);
  white-space: nowrap;
}

.logo-text .accent {
  color: var(--accent-primary, #00e701);
}

.logo-text .badge {
  font-size: 9px;
  font-family: var(--font-body, 'DM Sans', sans-serif);
  background: var(--accent-secondary, #1475e1);
  color: var(--color-on-dark, #fff);
  padding: 2px 6px;
  border-radius: var(--radius-sm, 4px);
  margin-left: 6px;
  letter-spacing: 0.05em;
}

/* Navigation */
.sidebar-nav {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.sidebar-section {
  margin-bottom: 8px;
}

.sidebar-section-title {
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: var(--text-tertiary, #7f8fa4);
  padding: 12px 10px 6px;
}

.sidebar-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  height: 40px;
  border-radius: var(--radius-md, 8px);
  cursor: pointer;
  transition: all var(--transition-fast, 150ms ease);
  position: relative;
  color: var(--text-secondary, #b1bad3);
  font-size: 13px;
  font-weight: 500;
  white-space: nowrap;
  border: none;
  width: 100%;
  text-align: left;
  text-decoration: none;
}

.sidebar-item:hover {
  background: var(--bg-tertiary, #213743);
  color: var(--text-primary, #fff);
}

.sidebar-item.active {
  background: rgba(0, 231, 1, 0.08);
  color: var(--text-primary, #fff);
  border-left: 3px solid var(--accent-primary, #00e701);
}

.sidebar-item.active .sidebar-item-icon {
  color: var(--accent-primary, #00e701);
}

.sidebar-item-icon {
  font-size: 16px;
  flex-shrink: 0;
  width: 20px;
  text-align: center;
}

.sidebar-label {
  flex: 1;
}

/* Mini state */
.admin-sidebar.mini .sidebar-item {
  justify-content: center;
  padding: 0;
}

.admin-sidebar.mini .sidebar-item .sidebar-item-icon {
  margin: 0;
}

/* License status indicator */
.license-status-bar {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  margin: 0 8px 4px;
  border-radius: var(--radius-md, 8px);
  font-size: 12px;
  font-weight: 500;
  flex-shrink: 0;
}

.license-status-bar.license-active {
  background: rgba(0, 231, 1, 0.08);
  color: #00e701;
}

.license-status-bar.license-grace {
  background: rgba(255, 167, 38, 0.1);
  color: #ffa726;
}

.license-status-bar.license-inactive {
  background: rgba(229, 57, 53, 0.1);
  color: #e53935;
}

.license-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
}

.license-active .license-dot {
  background: #00e701;
}

.license-grace .license-dot {
  background: #ffa726;
}

.license-inactive .license-dot {
  background: #e53935;
}

.license-dot.pulse {
  animation: licPulse 1.5s ease infinite;
}

@keyframes licPulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.4;
  }
}

.license-label {
  flex: 1;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.license-settings-link {
  font-size: 11px;
  font-weight: 600;
  text-decoration: none;
  opacity: 0.8;
  flex-shrink: 0;
  color: inherit;
}

.license-settings-link:hover {
  opacity: 1;
  text-decoration: underline;
}

/* Footer with admin info */
.admin-sidebar-footer {
  padding: 12px;
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.admin-avatar {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: var(--gradient-avatar, linear-gradient(135deg, #1475e1, #9b59b6));
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-on-dark, #fff);
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
}

.admin-footer-info {
  flex: 1;
  min-width: 0;
}

.admin-footer-name {
  font-size: 13px;
  color: var(--text-primary, #fff);
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.admin-footer-role {
  font-size: 11px;
  color: var(--text-tertiary, #7f8fa4);
}

/* Collapse button */
.sidebar-collapse-area {
  padding: 8px;
}

.sidebar-collapse-btn {
  width: 100%;
  padding: 8px;
  background: var(--bg-tertiary, #213743);
  border-radius: var(--radius-md, 8px);
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: var(--text-tertiary, #7f8fa4);
  font-size: 16px;
  transition: all var(--transition-fast, 150ms ease);
  border: none;
}

.sidebar-collapse-btn:hover {
  color: var(--text-primary, #fff);
  background: var(--bg-hover, #2a4455);
}

.collapse-icon {
  display: inline-block;
  transition: transform var(--transition-normal, 250ms ease);
}

.collapse-icon.flipped {
  transform: rotate(180deg);
}

/* Responsive: hide sidebar on mobile */
@media (max-width: 768px) {
  .admin-sidebar {
    display: none;
  }
}

/* =========================================================
   RTL overrides
   ========================================================= */
[dir='rtl'] .admin-sidebar {
  border-right: none;
  border-left: 1px solid rgba(255, 255, 255, 0.05);
}

[dir='rtl'] .sidebar-item {
  text-align: right;
}

[dir='rtl'] .sidebar-item.active {
  border-left: none;
  border-right: 3px solid var(--accent-primary, #00e701);
}

[dir='rtl'] .sidebar-section-title {
  text-align: right;
}

[dir='rtl'] .logo-text .badge {
  margin-left: 0;
  margin-right: 6px;
}

/* Collapse icon — in RTL sidebar is on the right; default should point right, .flipped points left */
[dir='rtl'] .collapse-icon {
  transform: rotate(180deg);
}

[dir='rtl'] .collapse-icon.flipped {
  transform: rotate(0deg);
}
</style>
